// Graphite and Zentrolink are immutable technical white-label keys. Each
// theme's user-facing brand (name, colors) is injected by the container
// before the application is served; the two themes never share assets and
// neither is inferred from the other's runtime display name.
export const BRAND_INDIVIDUALS = ["Graphite", "Zentrolink"];

const DEFAULT_ONEKEY_CSV = "keys.csv";
const MAX_DOWNLOAD_FILENAME_BYTES = 240;
// eslint-disable-next-line no-control-regex
const UNSAFE_FILENAME_CHARS = /[/\\\x00-\x1f\x7f]/g;

export function isBrandIndividual(individual) {
  return BRAND_INDIVIDUALS.includes(individual);
}

export function runtimeBrand() {
  if (typeof window === "undefined" || !window.__PROCESSING_BRAND__) {
    throw new Error("Runtime brand snapshot is unavailable");
  }
  return window.__PROCESSING_BRAND__;
}

export function brandName(individual) {
  return isBrandIndividual(individual) ? runtimeBrand().name : null;
}

export function sanitizeCsvFilename(name) {
  return String(name).replace(UNSAFE_FILENAME_CHARS, "_");
}

function truncateUtf8(value, maxBytes) {
  let result = "";
  let bytes = 0;
  for (const character of value) {
    const size = new TextEncoder().encode(character).length;
    if (bytes + size > maxBytes) break;
    result += character;
    bytes += size;
  }
  return result;
}

function boundedCsvFilename(prefix, baseName) {
  const safeBase = sanitizeCsvFilename(baseName);
  const extension = safeBase.toLowerCase().endsWith(".csv") ? ".csv" : "";
  const stem = extension ? safeBase.slice(0, -extension.length) : safeBase;
  const extensionBytes = new TextEncoder().encode(extension).length;
  return `${truncateUtf8(`${prefix}${stem}`, MAX_DOWNLOAD_FILENAME_BYTES - extensionBytes)}${extension}`;
}

// Keep Unicode letters and digits readable, but never let punctuation from a
// display name become a path/header delimiter in a browser download filename.
export function brandCsvPrefix(individual) {
  if (!isBrandIndividual(individual)) return "";
  const token = runtimeBrand().name
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "") || "brand";
  return `${token}-`;
}

export function onekeyCsvFilename(individual) {
  return isBrandIndividual(individual)
    ? boundedCsvFilename(brandCsvPrefix(individual), DEFAULT_ONEKEY_CSV)
    : DEFAULT_ONEKEY_CSV;
}

export function chartCsvFilename(individual, baseName) {
  if (!isBrandIndividual(individual)) return baseName;
  return boundedCsvFilename(brandCsvPrefix(individual), baseName);
}
